import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import PlayCircle from "@mui/icons-material/PlayCircle";
import Headphones from "@mui/icons-material/Headphones";
import MenuBook from "@mui/icons-material/MenuBook";
import AccessTime from "@mui/icons-material/AccessTime";
import {
  useStartExamMutation,
} from "@/store/api/user/examApi";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentAttempt } from "@/store/slices/examSlice";
import { selectIsAdmin } from "@/store/slices/authSlice";
import { toast } from "react-toastify";
import { useEffect, useRef } from "react";
import soundTestFile from "./sound-test.mp3";
import { ExportPdfButton } from "@/components/common";
import { downloadExamPdf } from "@/utils/pdfExport";

export default function WelcomeScreen({
  exam,
  setShowInstructions,
  collapseSidebar,
  setHasUserInteracted,
  setShowWelcomeScreen,
  setAudioVolume,
  // Optional overrides so the same welcome screen can be reused by the
  // public mock test runner (no /start mutation, no PDF export, etc.).
  onStart,
  // Called when the server reports this is an existing in-progress attempt.
  // The parent can then skip intro/audio and jump directly to resume state.
  onResumed,
  disableExport = false,
}) {
  const [startTest] = useStartExamMutation();
  const audioRef = useRef(null);
  const isAdmin = useSelector(selectIsAdmin);

  const dispatch = useDispatch();

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.addEventListener("volumechange", () => {
        // console.log("Volume changed to: ", audioRef.current.volume);
        setAudioVolume(audioRef.current.volume);
      });
    }
  }, [audioRef, setAudioVolume]);

  const onStartTest = async () => {
    // Public mock test (or any caller) can supply an `onStart` callback that
    // takes the place of the private exam's /start mutation + setCurrentAttempt
    // dispatch. We still drive the welcome → instructions transition + the
    // audio unlock side-effects here so the screen behaves identically.
    if (onStart) {
      try {
        const ok = await onStart();
        if (ok === false) return;
      } catch (_) {
        return;
      }
      setHasUserInteracted(true);
      setShowWelcomeScreen(false);
      setShowInstructions(true);
      collapseSidebar?.();
      return;
    }

    try {
      console.log("Starting / resuming attempt");
      const response = await startTest(exam?._id).unwrap();
      const attemptData = response?.data;
      dispatch(setCurrentAttempt(attemptData));
      collapseSidebar();
      setHasUserInteracted(true);

      if (response?.message === "Resuming existing attempt" && onResumed) {
        onResumed(attemptData);
        return;
      }

      // Normal new attempt transition: show instructions first.
      setShowWelcomeScreen(false);
      setShowInstructions(true);
    } catch (error) {
      console.log(error);
      toast.error(error?.data?.message);
    }
  };

  return (
    <Container maxWidth="lg">
      <Paper
        elevation={3}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "70vh",
          textAlign: "center",
          p: 4,
          mt: 4,
          borderRadius: 2,
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          {exam?.title}
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {exam?.description}
        </Typography>

        {/* Test instruction */}
        <Box
          sx={{
            my: 2,
            textAlign: "left",
            p: 2,
            borderRadius: 2,
            border: "1px solid #ccc",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography
            component="p"
            sx={{
              fontWeight: "bold",
              mb: 1,
              fontSize: "1.2rem",
              color: "#1976d2"
            }}
          >
            HƯỚNG DẪN LÀM BÀI
          </Typography>

          <Typography
            component="p"
            sx={{ textDecoration: "underline", fontWeight: "bold", mb: 1 }}
          >
            1. Bước 01: Đọc hướng dẫn làm bài
          </Typography>

          <Typography component="p" sx={{ mb: 1 }}>
            - Hệ thống giới thiệu thông tin bài thi và các công cụ làm bài. Thời
            gian tìm hiểu thông tin bài thi và các công cụ làm bài không tính
            trong thời gian làm bài.
          </Typography>

          <Typography component="p" sx={{ mb: 2 }}>
            - Nhấn nút Start Test sau khi đọc xong thông tin giới thiệu, kiểm
            tra âm thanh để bắt đầu làm bài thi.
          </Typography>

          <Typography
            component="p"
            sx={{ textDecoration: "underline", fontWeight: "bold", mb: 1 }}
          >
            2. Bước 02: Thí sinh bắt đầu làm bài theo thời gian quy định
          </Typography>

          <Typography component="p" sx={{ mb: 1 }}>
            - Trong trường hợp bài thi có phần thi Nghe-Nói, phần thi này được
            tự động chạy và chuyển câu khi bắt đầu làm bài. Thí sinh không được
            quay lại các câu hỏi chưa hoàn thành trong phần thi này.
          </Typography>

          <Typography component="p" sx={{ mb: 1 }}>
            - Thí sinh có thể đánh dấu các câu hỏi cần xem lại đối với phần thi
            Đọc và cần xem lại trước khi trả lời câu hỏi cuối cùng của phần thi
            Đọc.
          </Typography>

          <Typography component="p" sx={{ mb: 2 }}>
            - Thí sinh cần lưu ý sau khi chuyển qua phần thi khác thì không quay
            lại phần thi trước được.
          </Typography>

          <Typography
            component="p"
            sx={{ textDecoration: "underline", fontWeight: "bold", mb: 1 }}
          >
            3. Bước 03: Kết thúc bài thi
          </Typography>

          <Typography component="p" sx={{ mb: 2 }}>
            - Thí sinh bấm nút Submit để kết thúc bài thi và nhận kết quả.
          </Typography>

          <Typography
            component="p"
            sx={{ textDecoration: "underline", fontWeight: "bold", mb: 1 }}
          >
            4. Thí sinh lưu ý:
          </Typography>

          <Typography component="p" sx={{ mb: 1 }}>
            - Mỗi mã code chỉ được sử dụng 01 lần duy nhất. (Áp dụng với các bài
            thi thử sử dụng code)
          </Typography>

          <Typography component="p" sx={{ mb: 1 }}>
            - Thí sinh chỉ làm bài thi trên máy tính bàn hoặc laptop để đạt kết
            quả tốt nhất.
          </Typography>

          <Typography component="p" sx={{ mb: 1 }}>
            - Thí sinh cần đảm bảo thiết bị, đường truyền mạng, tai nghe có mic
            ổn định để làm bài.
          </Typography>

          <Typography component="p" sx={{ mb: 1 }}>
            - Trong trường hợp làm bài thi gặp các lỗi liên quan đến thiết bị,
            đường truyền mạng, trong thời gian làm bài kể từ khi kích hoạt code,
            thí sinh có thể làm lại bài thi bằng cách bấm vào nút Retake trên
            bài thi.
          </Typography>

          <Typography component="p" sx={{ mb: 1 }}>
            - Sau khi bấm Submit, thí sinh không thể Retake.
          </Typography>

          <Typography component="p" sx={{ mt: 2, fontStyle: "italic" }}>
            Chúc thí sinh hoàn thành bài thi với điểm số cao.
          </Typography>
        </Box>

        {/* Sound test */}
        <Box
          sx={{
            mt: 2,
            textAlign: "left",
            p: 2,
            borderRadius: 2,
            border: "1px solid #ccc",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Typography
            component="p"
            sx={{ fontWeight: "bold", mb: 1, fontSize: "1.2rem" }}
          >
            SOUND TEST
          </Typography>
          <Typography component="p" sx={{ mb: 1 }}>
            You are listening to a system audio test. You can adjust the volume
            as needed. If you can’t hear the audio, please check if your device
            is muted or if microphone access and audio playback permissions are
            disabled. If the issue persists, please contact customer service for
            support.
          </Typography>
          <audio
            controls
            style={{
              marginTop: "1rem",
              maxWidth: "100%",
              maxHeight: "200px",
              borderRadius: "4px",
              width: "100%",
            }}
            ref={audioRef}
            src={soundTestFile}
          />
        </Box>

        {/* Test Structure */}
        <Box
          sx={{
            my: 2,
            textAlign: "left",
            p: 2,
            borderRadius: 2,
            border: "1px solid #ccc",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            width: "100%",
          }}
        >
          <Typography
            component="h3"
            sx={{
              fontWeight: "bold",
              mb: 2,
              fontSize: "1.2rem",
              textAlign: "center",
            }}
          >
            Cấu Trúc Đề Thi
          </Typography>

          {/* Listening Section */}
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#1976d2" }}>
                  <TableCell
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Headphones />
                    Listening
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      textAlign: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                      }}
                    >
                      <AccessTime fontSize="small" />
                      45 phút
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      textAlign: "right",
                    }}
                  >
                    🎧
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>1</TableCell>
                  <TableCell>Part 1</TableCell>
                  <TableCell sx={{ textAlign: "right" }}>6 câu</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>2</TableCell>
                  <TableCell>Part 2</TableCell>
                  <TableCell sx={{ textAlign: "right" }}>25 câu</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>3</TableCell>
                  <TableCell>Part 3</TableCell>
                  <TableCell sx={{ textAlign: "right" }}>39 câu</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>4</TableCell>
                  <TableCell>Part 4</TableCell>
                  <TableCell sx={{ textAlign: "right" }}>30 câu</TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: "bold" }}></TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#1976d2" }}>
                    Tổng
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      textAlign: "right",
                      color: "#1976d2",
                    }}
                  >
                    100 câu
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Reading Section */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#ff9800" }}>
                  <TableCell
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <MenuBook />
                    Reading
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      textAlign: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                      }}
                    >
                      <AccessTime fontSize="small" />
                      75 phút
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      textAlign: "right",
                    }}
                  >
                    📖
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>5</TableCell>
                  <TableCell>Part 5</TableCell>
                  <TableCell sx={{ textAlign: "right" }}>30 câu</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>6</TableCell>
                  <TableCell>Part 6</TableCell>
                  <TableCell sx={{ textAlign: "right" }}>16 câu</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>7</TableCell>
                  <TableCell>Part 7</TableCell>
                  <TableCell sx={{ textAlign: "right" }}>54 câu</TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: "bold" }}></TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#ff9800" }}>
                    Tổng
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      textAlign: "right",
                      color: "#ff9800",
                    }}
                  >
                    100 câu
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 4, alignItems: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<PlayCircle fontSize="large" />}
            onClick={onStartTest}
            sx={{
              px: 4,
              py: 2,
              fontSize: "1.2rem",
              "& .MuiButton-startIcon": {
                "& > *:first-of-type": {
                  fontSize: "2rem",
                },
              },
            }}
          >
            Start Test
          </Button>
          <ExportPdfButton
            variant="outlined"
            size="large"
            isAdmin={isAdmin}
            onExport={() => downloadExamPdf(exam?._id, exam?.title)}
            onExportWithAnswers={isAdmin ? () => downloadExamPdf(exam?._id, exam?.title, true) : undefined}
            label="Export PDF"
            sx={{
              py: 2,
              px: 3,
              fontSize: "1rem",
              display: disableExport ? "none" : undefined,
            }}
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Click the button to start this examination, by clicking the button,
          timer will start and you can't pause it.
        </Typography>
      </Paper>
    </Container>
  );
}
